import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import {
  SLIDE_PADDING,
  SLIDE_CARD,
  SLIDE_SCROLL_BOTTOM_PAD,
  STORY_CARD_BG,
  NARRATIVE,
} from './constants';

export function CardCelebration({
  isWrapUp,
  className,
}: Readonly<{ isWrapUp: boolean; className?: string }>) {
  return (
    <Card
      className={cn(
        SLIDE_CARD,
        'relative overflow-hidden',
        STORY_CARD_BG,
        className
      )}
    >
      <CardContent
        className={cn(
          'relative flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-y-contain text-center',
          SLIDE_PADDING,
          SLIDE_SCROLL_BOTTOM_PAD
        )}
      >
        {/* ── Celebration Illustration ── */}
        <div className="flex min-h-0 w-full flex-1 flex-col items-center justify-center px-1 py-2">
          <div className="relative flex h-full min-h-[200px] w-full max-w-[20rem] items-center justify-center sm:max-w-[24rem]">
            <svg
              viewBox="0 0 220 220"
              className="h-full w-full [max-height:min(42dvh,360px)]"
              aria-hidden="true"
            >
              <defs>
                {/* Spotlight cone from top */}
                <radialGradient id="spotlight" cx="50%" cy="0%" r="100%" fx="50%" fy="0%">
                  <stop offset="0%"   stopColor="#fef9c3" stopOpacity="0.95" />
                  <stop offset="55%"  stopColor="#fde68a" stopOpacity="0.45" />
                  <stop offset="100%" stopColor="#fde68a" stopOpacity="0" />
                </radialGradient>

                {/* Soft ambient glow behind book */}
                <radialGradient id="book-glow" cx="50%" cy="50%" r="50%">
                  <stop offset="0%"   stopColor="#fef08a" stopOpacity="0.6" />
                  <stop offset="100%" stopColor="#fef08a" stopOpacity="0" />
                </radialGradient>

                {/* Book cover gradient */}
                <linearGradient id="cover-grad" x1="0" y1="0" x2="1" y2="1">
                  <stop offset="0%"   stopColor="#1e3a5f" />
                  <stop offset="100%" stopColor="#0f2340" />
                </linearGradient>

                {/* Book spine gradient */}
                <linearGradient id="spine-grad" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%"   stopColor="#152e4d" />
                  <stop offset="100%" stopColor="#0d1f35" />
                </linearGradient>

                {/* Gold border gradient */}
                <linearGradient id="gold-border" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%"   stopColor="#fde68a" />
                  <stop offset="50%"  stopColor="#f59e0b" />
                  <stop offset="100%" stopColor="#d97706" />
                </linearGradient>

                {/* Clip for spotlight cone */}
                <clipPath id="cone-clip">
                  <polygon points="110,0 30,220 190,220" />
                </clipPath>
              </defs>

              {/* ── Aura Light Rings ── */}
              <g style={{ transformOrigin: '110px 148px' }}>
                <circle cx="110" cy="148" r="45" fill="none" stroke="#fde68a" strokeWidth="2"
                  style={{ animation: 'aura-expand 4s 0s ease-out infinite' }} />
                <circle cx="110" cy="148" r="45" fill="none" stroke="#fef08a" strokeWidth="1.5"
                  style={{ animation: 'aura-expand 4s 1.3s ease-out infinite' }} />
                <circle cx="110" cy="148" r="45" fill="none" stroke="#fef9c3" strokeWidth="1"
                  style={{ animation: 'aura-expand 4s 2.6s ease-out infinite' }} />
              </g>

              {/* ── Spotlight cone ── */}
              <polygon
                points="110,0 30,230 190,230"
                fill="url(#spotlight)"
                style={{ animation: 'spotlight-pulse 3s ease-in-out infinite' }}
              />

              {/* ── Light rays ── */}
              {[-18,-9,0,9,18].map((angle, i) => (
                <line
                  key={i}
                  x1="110" y1="2"
                  x2={110 + Math.sin((angle * Math.PI) / 180) * 200}
                  y2="210"
                  stroke="#fef3c7"
                  strokeWidth={i === 2 ? 1.5 : 0.8}
                  strokeOpacity={i === 2 ? 0.25 : 0.12}
                  style={{
                    animation: `ray-flicker 3s ${i * 0.3}s ease-in-out infinite`,
                  }}
                />
              ))}

              {/* ── Glow behind Quran ── */}
              <ellipse
                cx="110" cy="148"
                rx="52" ry="18"
                fill="url(#book-glow)"
                style={{ animation: 'glow-pulse 3s ease-in-out infinite' }}
              />

              {/* ── Quran Book ── */}
              <g style={{ animation: 'float-book 4s ease-in-out infinite' }}>
                {/* Page spread (open pages) */}
                <path d="M87,100 Q110,96 110,130 L110,172 Q110,172 87,170 Z"
                  fill="#f9f6ef" />
                <path d="M133,100 Q110,96 110,130 L110,172 Q110,172 133,170 Z"
                  fill="#f0ece2" />

                {/* Page lines left */}
                {[110,118,126,134,142,150,158].map((y, i) => (
                  <line key={i} x1="92" y1={y} x2="108" y2={y}
                    stroke="#d1c9b8" strokeWidth="1" opacity="0.7" />
                ))}
                {/* Page lines right */}
                {[110,118,126,134,142,150,158].map((y, i) => (
                  <line key={i} x1="112" y1={y} x2="128" y2={y}
                    stroke="#d1c9b8" strokeWidth="1" opacity="0.7" />
                ))}

                {/* Spine */}
                <rect x="107" y="96" width="6" height="78" rx="1"
                  fill="url(#spine-grad)" />

                {/* Left cover (closed back) */}
                <path d="M87,100 L76,104 L76,174 L87,170 Z"
                  fill="url(#cover-grad)" />
                {/* Right cover (closed back) */}
                <path d="M133,100 L144,104 L144,174 L133,170 Z"
                  fill="url(#cover-grad)" />

                {/* Gold border frame on covers */}
                <rect x="78" y="104" width="9" height="68" rx="0"
                  fill="none" stroke="url(#gold-border)" strokeWidth="0.8" />
                <rect x="133" y="104" width="9" height="68" rx="0"
                  fill="none" stroke="url(#gold-border)" strokeWidth="0.8" />

                {/* Gold ornament on spine */}
                <rect x="107.5" y="99" width="5" height="1.5" rx="0.5" fill="#f59e0b" />
                <rect x="107.5" y="171" width="5" height="1.5" rx="0.5" fill="#f59e0b" />

                {/* Arabic "بسم الله" stylized text on left page */}
                <text
                  x="99" y="122"
                  textAnchor="middle"
                  fontSize="5.5"
                  fontFamily="serif"
                  fill="#8b7355"
                  opacity="0.85"
                >Quran</text>

                {/* Ornamental dot on right page */}
                <circle cx="121" cy="128" r="4" fill="none"
                  stroke="#c9a96e" strokeWidth="0.8" opacity="0.6" />
                <circle cx="121" cy="128" r="1.5" fill="#c9a96e" opacity="0.5" />

                {/* Glow shimmer across pages */}
                <path d="M87,100 Q110,96 133,100 L133,104 Q110,100 87,104 Z"
                  fill="#fef9c3" opacity="0.5"
                  style={{ animation: 'page-shimmer 3s ease-in-out infinite' }}
                />
              </g>

              {/* ── Shadow under book ── */}
              <ellipse cx="110" cy="176" rx="28" ry="5"
                fill="#000" opacity="0.08" />
            </svg>

            <style>{`
              @keyframes aura-expand {
                0%   { transform: scale(0.6); opacity: 0.8; }
                100% { transform: scale(2.2); opacity: 0; }
              }
              @keyframes float-book {
                0%,100% { transform: translateY(0); }
                50%      { transform: translateY(-7px); }
              }
              @keyframes spotlight-pulse {
                0%,100% { opacity: 0.75; }
                50%      { opacity: 1; }
              }
              @keyframes ray-flicker {
                0%,100% { opacity: 0.6; }
                40%      { opacity: 1; }
                70%      { opacity: 0.7; }
              }
              @keyframes glow-pulse {
                0%,100% { opacity: 0.55; }
                50%      { opacity: 1; }
              }
              @keyframes page-shimmer {
                0%,100% { opacity: 0.3; }
                50%      { opacity: 0.65; }
              }
              @keyframes page-curl {
                0%,100% { transform: rotateY(0deg) scaleX(1); opacity: 0.12; }
                50%     { transform: rotateY(-40deg) scaleX(0.85); opacity: 0.25; }
              }
              @keyframes arrow-left {
                0%,100% { opacity: 0.2; transform: translateX(2px); }
                50%      { opacity: 1;   transform: translateX(-3px); }
              }
            `}</style>
          </div>
        </div>

        {/* ── Text ── */}
        <div className="mt-2 shrink-0 space-y-1.5 px-1 sm:space-y-2">
          <h1 className="bg-gradient-to-r from-foreground to-foreground/75 bg-clip-text text-xl font-bold tracking-tight text-transparent sm:text-3xl animate-in fade-in duration-500">
            {isWrapUp ? 'อัลฮัมดุลิลลาฮ์' : 'มาชาอัลลอฮ์!'}
          </h1>
          <p className="text-sm font-semibold text-foreground/90 sm:text-base animate-in fade-in duration-500 delay-75">
            {isWrapUp ? 'ทุกหน้าที่อ่านมีค่ายิ่ง' : 'คุณทำสำเร็จแล้ว!'}
          </p>
          <p className="mx-auto max-w-sm text-xs leading-relaxed text-muted-foreground sm:text-sm animate-in fade-in duration-500 delay-150">
            {isWrapUp ? NARRATIVE.celebration.wrapUp : NARRATIVE.celebration.complete}
          </p>
        </div>

        {/* ── Drag-to-Flip Hint ── */}
        <div
          className="mt-3 flex w-full max-w-sm shrink-0 flex-col items-center gap-2 self-center rounded-2xl border border-primary/20 bg-background/70 px-4 py-3 shadow-sm backdrop-blur-md animate-in fade-in slide-in-from-bottom-2 duration-500 delay-300 sm:mt-4 sm:py-4"
          role="status"
          aria-live="polite"
        >
          <p className="text-[11px] font-semibold uppercase tracking-widest text-primary/80 sm:text-xs">
            ขั้นตอนถัดไป
          </p>

          <div className="relative flex w-full items-center justify-center overflow-hidden rounded-xl bg-primary/8 py-3">
            <svg
              viewBox="0 0 60 40"
              className="h-12 w-16 text-primary"
              aria-hidden="true"
            >
              {/* Open book base */}
              <path
                d="M10,12 Q40,8 40,40 L40,52 Q40,52 10,48 Z"
                fill="currentColor"
                fillOpacity="0.08"
                stroke="currentColor"
                strokeWidth="1"
                strokeOpacity="0.3"
              />
              <path
                d="M70,12 Q40,8 40,40 L40,52 Q40,52 70,48 Z"
                fill="currentColor"
                fillOpacity="0.06"
                stroke="currentColor"
                strokeWidth="1"
                strokeOpacity="0.2"
              />
              {/* Spine */}
              <line x1="40" y1="8" x2="40" y2="52" stroke="currentColor" strokeWidth="1.5" opacity="0.4" />

              {/* Page flipping (animated) */}
              <path
                d="M70,12 Q55,10 42,40 L42,52 Q55,50 70,48 Z"
                fill="currentColor"
                opacity="0.12"
                stroke="currentColor"
                strokeWidth="1"
                style={{
                  transformOrigin: '42px 30px',
                  animation: 'page-curl 2.4s ease-in-out infinite',
                }}
              />

              {/* Page lines */}
              {[22, 28, 34, 40].map((y, i) => (
                <line key={i} x1="16" y1={y} x2="36" y2={y}
                  stroke="currentColor" strokeWidth="0.8" opacity="0.15" />
              ))}
            </svg>

            {/* Drag arrow indicator */}
            <div className="ml-2 flex items-center gap-1">
              <svg viewBox="0 0 24 16" className="h-4 w-6 text-primary" aria-hidden="true">
                {[16, 8, 0].map((x, i) => (
                  <path
                    key={i}
                    d={`M${x + 6},8 L${x},8 M${x + 3},4 L${x - 1},8 L${x + 3},12`}
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{
                      animation: `arrow-left 1.6s ${i * 0.18}s ease-in-out infinite`,
                    }}
                  />
                ))}
              </svg>
            </div>
          </div>

          <p className="text-center text-[11px] leading-relaxed text-muted-foreground sm:text-xs">
            ลากนิ้วไปทางซ้ายเพื่อ
            <span className="font-medium text-foreground"> พลิกหน้าหนังสือ</span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
